import './lib/env'

import { Toaster } from '@/components/ui/toaster'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { LazyMotion, domAnimation } from 'motion/react'
import { ThemeProvider } from 'next-themes'
import { SkeletonTheme } from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { StrictMode, Suspense, lazy } from 'react'
import type { JSX } from 'react'
import { createRoot } from 'react-dom/client'

import { env } from '@/lib/env'
import { queryClient } from '@/lib/query-client'
import { routeTree } from './routeTree.gen'
import './styles/globals.css'

// ── Chunk load failures (ej. nuevo deploy con hashes distintos) ──────────────
// Vite emite este evento cuando un import() dinámico falla. Sin este handler
// la app queda en pantalla en blanco sin posibilidad de recuperarse.
// La recarga es de UN SOLO INTENTO. Si el chunk sigue sin existir tras recargar, el fallo es
// permanente (deploy a medias, caché de deps de Vite rancia tras dejar el dev server días
// apagado) y recargar en bucle deja la pantalla en blanco parpadeando, sin error visible ni
// forma de salir. El flag vive en sessionStorage para que una pestaña nueva vuelva a tener su
// intento.
const PRELOAD_RETRY_KEY = 'abk_preload_retry'

window.addEventListener('vite:preloadError', (event) => {
  let alreadyRetried = true
  try {
    alreadyRetried = sessionStorage.getItem(PRELOAD_RETRY_KEY) === '1'
    if (!alreadyRetried) sessionStorage.setItem(PRELOAD_RETRY_KEY, '1')
  } catch {
    // Modo privado o storage bloqueado: sin forma de recordar el intento, no se recarga.
  }

  if (alreadyRetried) {
    // Dejar que el error se propague: es preferible que el ErrorBoundary de la ruta pinte
    // "Algo salió mal" antes que un bucle de recargas en blanco.
    console.error('[abakoo] Fallo persistente al cargar un chunk, se omite la recarga.', event)
    return
  }
  window.location.reload()
})

// Primera carga completa sin incidentes: se libera el intento para el próximo deploy.
window.addEventListener('load', () => {
  try {
    sessionStorage.removeItem(PRELOAD_RETRY_KEY)
  } catch {
    // Sin storage no hay nada que limpiar.
  }
})

// ── Restauración desde bfcache (Back/Forward Cache) ─────────────────────────
// iOS Safari y navegadores de escritorio congelan pestañas en segundo plano y
// las restauran con bfcache. React + Zustand pueden quedar en estado
// inconsistente tras este proceso, resultando en pantalla en blanco.
// Solución: si la pestaña fue ocultada (app switch, no navegación Back) antes
// de la restauración, recargamos para garantizar un estado limpio.
let hiddenAt: number | null = null

document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    hiddenAt = Date.now()
  } else {
    hiddenAt = null
  }
})

window.addEventListener('pageshow', (event: PageTransitionEvent) => {
  // event.persisted = true  →  página restaurada desde bfcache (no recarga normal)
  // hiddenAt !== null       →  la página fue ocultada (switch de app), no navegación Back
  if (event.persisted && hiddenAt !== null) {
    window.location.reload()
  }
})

// ── Devtools: carga lazy para no bloquear el bundle inicial ─────────────────
// En producción, el tree-shaking de Vite elimina estas líneas completamente
// porque env.VITE_ENVIRONMENT === 'develop' se evalúa a false en build time.
// En develop, se cargan como chunks separados después de que React renderiza.
const ReactQueryDevtools =
  env.VITE_ENVIRONMENT === 'develop'
    ? lazy(() =>
        import('@tanstack/react-query-devtools').then((m) => ({
          default: m.ReactQueryDevtools,
        })),
      )
    : null

// ── Pending component para rutas en estado de carga ──────────────────────────
// Sin esto, si el router entra en estado `pending` (ej. re-ejecutando beforeLoad)
// la pantalla queda en blanco. Este spinner reemplaza ese vacío.
function RouterPendingFallback(): JSX.Element {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center">
      <div className="size-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  )
}

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // 300 ms antes de disparar el pre-carga al hacer hover sobre un link.
  // El default (50 ms) es demasiado agresivo y causa 401s en la consola
  // al pasar el cursor sobre links en rutas públicas (/_public beforeLoad).
  defaultPreloadDelay: 300,
  // Mostrar spinner si la carga toma más de 200 ms (equilibrio entre flash y feedback).
  // Las rutas públicas tienen beforeLoad síncrono → nunca llegan a 200 ms.
  // Las rutas protegidas con refresh de token (~300-800 ms) sí muestran el spinner.
  defaultPendingComponent: RouterPendingFallback,
  defaultPendingMs: 200,
  defaultPendingMinMs: 300,
})

// Registrar el tipo del router para type safety end-to-end con TanStack Router
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Elemento #root no encontrado en el DOM')

createRoot(rootElement).render(
  <StrictMode>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      {/* LazyMotion carga solo domAnimation (~4.6 KB) en lugar del bundle completo de Motion */}
      <LazyMotion features={domAnimation} strict>
        {/* Tema de los skeletons alimentado por los tokens OKLCH: los colores van
            como `var(...)` para que sigan al tema sin re-renderizar nada. El ciclo
            de 1.2 s entra en el rango de 1–1.5 s y usa la misma velocidad que el
            resto de la plataforma. */}
        <SkeletonTheme
          baseColor="var(--skeleton-base)"
          highlightColor="var(--skeleton-sheen)"
          duration={1.2}
          borderRadius="var(--radius-md)"
        >
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
            {ReactQueryDevtools !== null && (
              <Suspense>
                <ReactQueryDevtools initialIsOpen={false} />
              </Suspense>
            )}
          </QueryClientProvider>
        </SkeletonTheme>
      </LazyMotion>
      {/* FUERA de LazyMotion, a propósito. Sileo renderiza componentes `motion.*`
          completos (`motion.rect`), y en modo `strict` LazyMotion lanza error al
          encontrar uno — sin error boundary, eso desmonta TODA la app al primer
          toast. Dentro de LazyMotion `strict` no puede vivir; fuera, `strict`
          sigue protegiendo al resto. Solo necesita ThemeProvider (`useTheme`). */}
      <Toaster />
    </ThemeProvider>
  </StrictMode>,
)
